/**
 * 周期生命周期演化图 V2 — 仅前端演示数据。
 * 阶段区间、涨幅、板位、事件都是写死的展示配置，不是判断规则。
 * 不要把这里的数字接到正式行情或交易逻辑上。
 */
export const DATA_MODE = 'STATIC_FRONTEND_MOCK_ONLY'

export const CYCLE_META = {
  cycleId: 'demo-culture-media',
  cycleName: '文化传媒演示周期',
  cycleStartDate: '2025-09-22',
  cycleStatus: '演示',
  dataCutoffDate: '2025-10-16',
  catchupMaxBoard: 6,
  kingdomName: '文化传媒(KPL)'
}

/** 展示用阶段色块。边界来自演示配置，页面不会用天数去推断阶段。 */
export const STAGE_BANDS = [
  { id: 'rise', name: '兴起阶段', from: 0, to: 2, color: 'rgba(64, 158, 255, 0.16)' },
  { id: 'expand', name: '扩张阶段', from: 3, to: 7, color: 'rgba(103, 194, 58, 0.16)' },
  { id: 'stall', name: '停滞阶段', from: 8, to: 11, color: 'rgba(230, 162, 60, 0.20)' },
  { id: 'age', name: '衰老阶段', from: 12, to: 24, color: 'rgba(245, 108, 108, 0.14)' }
]

export const RANGE_OPTIONS = [
  { id: 'd10', label: '10日', lastDay: 10 },
  { id: 'd15', label: '15日', lastDay: 15 },
  { id: 'd20', label: '20日', lastDay: 20 },
  { id: 'all', label: '全周期', lastDay: 24 }
]

export const D0_SOURCES = [
  { id: 'firstBoard', label: '首板日' },
  { id: 'leaderConfirm', label: '龙头确认日' },
  { id: 'breakHigh', label: '突破前高日' },
  { id: 'manualDate', label: '人工指定日期' }
]

/** 切换起始点时换一套静态示例，不是重新计算生命周期。 */
export const D0_SCENARIOS = {
  firstBoard: { returnFactor: 1, note: '演示：D0 取首板日' },
  leaderConfirm: { returnFactor: 0.86, note: '演示：D0 改到龙头确认日，涨幅按另一套静态比例展示' },
  breakHigh: { returnFactor: 1.06, note: '演示：D0 改到突破前高日，涨幅按另一套静态比例展示' },
  manualDate: { returnFactor: 0.78, note: '演示：D0 为人工指定日期，涨幅按另一套静态比例展示' }
}

export const POOL_OPTIONS = [
  { id: 'members', label: '当前王国成员' },
  { id: 'all', label: '全部周期股票' },
  { id: 'manual', label: '人工选择' }
]

export const KINGDOM_OPTIONS = [
  { id: 'current', label: '当前王国' },
  { id: 'all', label: '全部板块' },
  { id: 'named', label: '指定王国' }
]

export const EVENT_TYPES = [
  { type: 'accelerate', name: '加速扩张', color: '#f56c6c', icon: 'el-icon-top', desc: '出现连续大阳线，连板高度快速提升' },
  { type: 'stallHigh', name: '高位停滞', color: '#e6a23c', icon: 'el-icon-warning-outline', desc: '涨幅放缓，进入高位震荡，通常连续3日内涨幅 < 10%' },
  { type: 'ageSignal', name: '衰老信号', color: '#e85d4c', icon: 'el-icon-warning', desc: '高位出现放量滞涨，板位无法继续提升' },
  { type: 'volumeBreak', name: '断板爆量', color: '#f56c6c', icon: 'el-icon-warning', desc: '出现断板且成交额显著放大' },
  { type: 'nextOpenLow', name: '次日低开', color: '#409eff', icon: 'el-icon-bottom', desc: '断板次日低开，跌幅超过 -3%' },
  { type: 'nextDeep', name: '次日深水', color: '#e879f9', icon: 'el-icon-bottom', desc: '次日盘中大幅下跌，跌幅超过 -8%' },
  { type: 'nextLimitDown', name: '次日跌停', color: '#c45656', icon: 'el-icon-remove-outline', desc: '次日收盘跌停' },
  { type: 'nextRepair', name: '次日修复', color: '#67c23a', icon: 'el-icon-circle-check', desc: '次日重新涨停或明显修复' },
  { type: 'volumeHigh', name: '高位爆量', color: '#e6a23c', icon: 'el-icon-warning-outline', desc: '高位换手显著放大' }
]

export const STATUS_OPTIONS = ['活跃', '扩张', '停滞', '转弱', '衰老观察', '已结束']

export const ROLE_OPTIONS = ['王国龙', '趋势龙', '连板龙', '补涨', '其他']

const DAY_COUNT = 25

function series(returns, boards) {
  if (returns.length !== DAY_COUNT || boards.length !== DAY_COUNT) {
    throw new Error('演示轨迹长度必须覆盖 D0–D24')
  }
  return returns.map((cumulativeReturn, dayIndex) => ({
    dayIndex,
    tradeDate: `D${dayIndex}`,
    cumulativeReturn,
    dailyBoardHeight: boards[dayIndex]
  }))
}

export const STOCKS = [
  {
    code: '600825',
    name: '新华传媒',
    role: '王国龙',
    theme: '文化传媒(KPL)',
    kingdomId: 'culture',
    inPool: true,
    color: '#f25c54',
    currentStage: '衰老阶段',
    stagnationDay: 'D8',
    agingDay: 'D12',
    cycleStatus: '转弱',
    statusNote: '高位滞涨，进入衰老阶段，需等待换手。',
    totalReturn: 112.5,
    maxBoard: 7,
    currentBoard: 3,
    volumeHighCount: 1,
    volumeBreakCount: 1,
    nextOpenLowCount: 1,
    nextDeepCount: 0,
    nextRepairCount: 0,
    stagnationPoints: [
      'D8起连续3日涨幅放缓',
      '高位换手，板位未能继续提升',
      '成交额放大但换手率上升'
    ],
    agingPoints: [
      'D12出现断板且次日低开',
      '断板后未能快速修复',
      '补涨高度下降，红盘率回落'
    ],
    points: series(
      [0, 12, 22, 33.02, 46, 55, 63.75, 80, 92.07, 101, 109.73, 111.2, 112.5, 95.2, 101, 112.5, 108, 104, 99, 96, 93, 90, 88, 86, 84],
      [1, 2, 3, 3, 4, 5, 5, 6, 7, 7, 7, 7, 0, 1, 2, 3, 2, 1, 1, 0, 1, 0, 0, 0, 0]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 8.2, turnover: 12.5, event: '首板启动' },
      { dayIndex: 3, stage: '扩张', amount: 18.6, turnover: 24.1, event: '加速扩张' },
      { dayIndex: 6, stage: '扩张', amount: 28.9, turnover: 31.2, event: '高位放量' },
      { dayIndex: 8, stage: '停滞', amount: 35.6, turnover: 33.7, event: '高位停滞' },
      { dayIndex: 10, stage: '停滞', amount: 42.1, turnover: 35.8, event: '横盘震荡' },
      { dayIndex: 12, stage: '衰老', amount: 38.4, turnover: 29.1, event: '断板爆量' },
      { dayIndex: 13, stage: '衰老', amount: 31.2, turnover: 26.3, event: '次日低开' },
      { dayIndex: 15, stage: '衰老', amount: 22.6, turnover: 22.1, event: '持续回落' }
    ]
  },
  {
    code: '002242',
    name: '九阳股份',
    role: '趋势龙',
    theme: '文化传媒(KPL)',
    kingdomId: 'culture',
    inPool: true,
    color: '#3b82f6',
    currentStage: '停滞阶段',
    stagnationDay: 'D9',
    agingDay: '',
    cycleStatus: '停滞',
    statusNote: '高度停在高位后回落，还没进入衰老。',
    totalReturn: 85.3,
    maxBoard: 6,
    currentBoard: 2,
    volumeHighCount: 1,
    volumeBreakCount: 0,
    nextOpenLowCount: 1,
    nextDeepCount: 0,
    nextRepairCount: 1,
    stagnationPoints: ['D9后涨幅放缓，板位从6B回落到2B', '高位换手增加，高度没有再抬'],
    agingPoints: ['演示：尚未配置衰老起点'],
    points: series(
      [0, 6, 12, 20, 30, 42, 55, 68, 76, 85.3, 84, 82, 80, 76, 78, 85.3, 83, 81, 79, 77, 75, 74, 72, 70, 68],
      [1, 1, 2, 3, 4, 5, 6, 6, 6, 6, 5, 4, 3, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 1]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 4.1, turnover: 8.2, event: '首板启动' },
      { dayIndex: 6, stage: '扩张', amount: 12.4, turnover: 18.6, event: '加速扩张' },
      { dayIndex: 9, stage: '停滞', amount: 16.8, turnover: 22.4, event: '高位停滞' },
      { dayIndex: 15, stage: '停滞', amount: 9.2, turnover: 14.1, event: '次日修复' }
    ]
  },
  {
    code: '000701',
    name: '吉大',
    role: '趋势龙',
    theme: '文化传媒(KPL)',
    kingdomId: 'culture',
    inPool: true,
    color: '#ec4899',
    currentStage: '扩张阶段',
    stagnationDay: 'D10',
    agingDay: '',
    cycleStatus: '扩张',
    statusNote: '仍在扩张，高度还维持在 4B。',
    totalReturn: 61.1,
    maxBoard: 5,
    currentBoard: 4,
    volumeHighCount: 0,
    volumeBreakCount: 0,
    nextOpenLowCount: 0,
    nextDeepCount: 0,
    nextRepairCount: 1,
    stagnationPoints: ['D10后单日涨幅变小，仍保持4B'],
    agingPoints: ['演示：尚未配置衰老起点'],
    points: series(
      [0, 4, 9, 16, 24, 33, 42, 50, 55, 58, 61.1, 60, 59, 58, 60, 61.1, 60, 59, 58, 57, 56, 55, 54, 53, 52],
      [1, 1, 2, 2, 3, 4, 5, 5, 5, 4, 4, 4, 4, 4, 4, 4, 4, 3, 3, 3, 2, 2, 2, 2, 1]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 3.2, turnover: 9.4, event: '首板启动' },
      { dayIndex: 4, stage: '扩张', amount: 8.8, turnover: 16.2, event: '加速扩张' },
      { dayIndex: 10, stage: '停滞', amount: 7.1, turnover: 15.5, event: '高位停滞' }
    ]
  },
  {
    code: '000530',
    name: '冰山',
    role: '连板龙',
    theme: '文化传媒(KPL)',
    kingdomId: 'culture',
    inPool: true,
    color: '#8b5cf6',
    currentStage: '停滞阶段',
    stagnationDay: 'D9',
    agingDay: '',
    cycleStatus: '停滞',
    statusNote: '涨幅放缓，板位停在 3B。',
    totalReturn: 42.6,
    maxBoard: 4,
    currentBoard: 3,
    volumeHighCount: 0,
    volumeBreakCount: 0,
    nextOpenLowCount: 1,
    nextDeepCount: 0,
    nextRepairCount: 0,
    stagnationPoints: ['D9后高度停在3B–4B，涨幅放缓'],
    agingPoints: ['演示：尚未配置衰老起点'],
    points: series(
      [0, 3, 7, 12, 18, 24, 30, 35, 39, 42.6, 41, 40, 38, 37, 39, 42.6, 41, 40, 38, 37, 36, 35, 34, 33, 32],
      [1, 1, 2, 2, 3, 4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 1, 1, 1, 1]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 2.4, turnover: 7.1, event: '首板启动' },
      { dayIndex: 5, stage: '扩张', amount: 6.6, turnover: 14.8, event: '加速扩张' },
      { dayIndex: 9, stage: '停滞', amount: 5.1, turnover: 13.2, event: '高位停滞' }
    ]
  },
  {
    code: '000678',
    name: '襄阳',
    role: '补涨',
    theme: '文化传媒(KPL)',
    kingdomId: 'culture',
    inPool: true,
    color: '#22c55e',
    currentStage: '兴起阶段',
    stagnationDay: '',
    agingDay: '',
    cycleStatus: '活跃',
    statusNote: '补涨还在跟随，没有停滞和衰老起点。',
    totalReturn: 28.6,
    maxBoard: 2,
    currentBoard: 1,
    volumeHighCount: 0,
    volumeBreakCount: 0,
    nextOpenLowCount: 0,
    nextDeepCount: 0,
    nextRepairCount: 0,
    stagnationPoints: ['演示：尚未配置停滞起点'],
    agingPoints: ['演示：尚未配置衰老起点'],
    points: series(
      [0, 2, 4, 6, 8, 11, 13, 15, 17, 19, 21, 23, 24, 26, 27, 28.6, 28, 27, 26, 25, 24, 23, 22, 21, 20],
      [1, 1, 2, 2, 1, 1, 1, 2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 1.8, turnover: 6.4, event: '首板启动' },
      { dayIndex: 2, stage: '兴起', amount: 2.2, turnover: 8.1, event: '加速扩张' },
      { dayIndex: 15, stage: '兴起', amount: 1.6, turnover: 5.5, event: '持续跟随' }
    ]
  },
  {
    code: 'D00006',
    name: '其他演示',
    role: '其他',
    theme: '其他板块',
    kingdomId: 'other',
    inPool: false,
    color: '#9ca3af',
    currentStage: '兴起阶段',
    stagnationDay: '',
    agingDay: '',
    cycleStatus: '活跃',
    statusNote: '演示用的其他个股，不在当前王国成员里。',
    totalReturn: 15.4,
    maxBoard: 2,
    currentBoard: 1,
    volumeHighCount: 0,
    volumeBreakCount: 0,
    nextOpenLowCount: 0,
    nextDeepCount: 1,
    nextRepairCount: 0,
    stagnationPoints: ['演示：尚未配置停滞起点'],
    agingPoints: ['演示：尚未配置衰老起点'],
    points: series(
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15.4, 15, 14, 13, 12, 11, 10, 9, 8, 7],
      [1, 1, 1, 2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0]
    ),
    keyNodes: [
      { dayIndex: 0, stage: '兴起', amount: 0.8, turnover: 4.2, event: '首板启动' },
      { dayIndex: 15, stage: '兴起', amount: 0.6, turnover: 3.1, event: '次日深水' }
    ]
  }
]

export const EVENTS = [
  {
    eventId: 'xh-acc',
    stockCode: '600825',
    dayIndex: 3,
    eventType: 'accelerate',
    boardHeight: 3,
    eventDescription: '出现连续大阳线，连板高度快速提升。演示标记。',
    nextDayOpenReturn: 3.2,
    nextDayLowReturn: -1.1,
    nextDayCloseReturn: 6.4,
    nextDayRepairStatus: '修复'
  },
  {
    eventId: 'xh-stall',
    stockCode: '600825',
    dayIndex: 8,
    eventType: 'stallHigh',
    boardHeight: 7,
    eventDescription: '高位换手，板位停在7B，涨幅放缓。演示标记。',
    nextDayOpenReturn: 0.4,
    nextDayLowReturn: -2.2,
    nextDayCloseReturn: 1.1,
    nextDayRepairStatus: '震荡'
  },
  {
    eventId: 'xh-vol',
    stockCode: '600825',
    dayIndex: 8,
    eventType: 'volumeHigh',
    boardHeight: 7,
    eventDescription: '高位成交额放大，换手率升到33.7%。演示标记。',
    nextDayOpenReturn: 0.4,
    nextDayLowReturn: -2.2,
    nextDayCloseReturn: 1.1,
    nextDayRepairStatus: '震荡'
  },
  {
    eventId: 'xh-age',
    stockCode: '600825',
    dayIndex: 12,
    eventType: 'ageSignal',
    boardHeight: 0,
    eventDescription: '断板前高度7B。断板当日不再顺延原连板数。演示标记。',
    nextDayOpenReturn: -4.6,
    nextDayLowReturn: -7.2,
    nextDayCloseReturn: -5.1,
    nextDayRepairStatus: '未修复'
  },
  {
    eventId: 'xh-break',
    stockCode: '600825',
    dayIndex: 12,
    eventType: 'volumeBreak',
    boardHeight: 0,
    eventDescription: '断板同时成交额仍高。演示标记。',
    nextDayOpenReturn: -4.6,
    nextDayLowReturn: -7.2,
    nextDayCloseReturn: -5.1,
    nextDayRepairStatus: '未修复'
  },
  {
    eventId: 'xh-low',
    stockCode: '600825',
    dayIndex: 13,
    eventType: 'nextOpenLow',
    boardHeight: 1,
    eventDescription: '断板次日低开，新一段从1B重新数，不接原来的7B。演示标记。',
    nextDayOpenReturn: -4.6,
    nextDayLowReturn: -6.1,
    nextDayCloseReturn: -2.4,
    nextDayRepairStatus: '未修复'
  },
  {
    eventId: 'jy-stall',
    stockCode: '002242',
    dayIndex: 9,
    eventType: 'stallHigh',
    boardHeight: 6,
    eventDescription: '高度停在6B后开始回落。演示标记。',
    nextDayOpenReturn: -3.4,
    nextDayLowReturn: -5.2,
    nextDayCloseReturn: 1.6,
    nextDayRepairStatus: '修复'
  },
  {
    eventId: 'jy-repair',
    stockCode: '002242',
    dayIndex: 15,
    eventType: 'nextRepair',
    boardHeight: 2,
    eventDescription: '回落后次日收盘修复。演示标记。',
    nextDayOpenReturn: 1.2,
    nextDayLowReturn: -0.8,
    nextDayCloseReturn: 4.4,
    nextDayRepairStatus: '修复'
  },
  {
    eventId: 'jd-acc',
    stockCode: '000701',
    dayIndex: 4,
    eventType: 'accelerate',
    boardHeight: 3,
    eventDescription: '连板从2B抬到3B。演示标记。',
    nextDayOpenReturn: 2.2,
    nextDayLowReturn: 0.4,
    nextDayCloseReturn: 5.1,
    nextDayRepairStatus: '修复'
  },
  {
    eventId: 'bs-low',
    stockCode: '000530',
    dayIndex: 10,
    eventType: 'nextOpenLow',
    boardHeight: 3,
    eventDescription: '停滞段次日低开。演示标记。',
    nextDayOpenReturn: -3.8,
    nextDayLowReturn: -4.9,
    nextDayCloseReturn: -1.2,
    nextDayRepairStatus: '未修复'
  },
  {
    eventId: 'ot-deep',
    stockCode: 'D00006',
    dayIndex: 15,
    eventType: 'nextDeep',
    boardHeight: 1,
    eventDescription: '演示股次日深水。演示标记。',
    nextDayOpenReturn: -2.1,
    nextDayLowReturn: -9.4,
    nextDayCloseReturn: -6.6,
    nextDayRepairStatus: '未修复'
  }
]

export function eventMeta(type) {
  return EVENT_TYPES.find((item) => item.type === type) || EVENT_TYPES[0]
}

export function stockByCode(code) {
  return STOCKS.find((item) => item.code === code) || STOCKS[0]
}

export function applyScenario(value, sourceId) {
  if (value == null || value === '') return null
  const factor = (D0_SCENARIOS[sourceId] || D0_SCENARIOS.firstBoard).returnFactor
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.round(n * factor * 10) / 10
}

export function visibleBands(lastDay) {
  return STAGE_BANDS.filter((band) => band.from <= lastDay).map((band) => ({
    ...band,
    to: Math.min(band.to, lastDay)
  }))
}

export function dayLabels(lastDay) {
  return Array.from({ length: lastDay + 1 }, (_, index) => `D${index}`)
}
